import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, saveToken, clearToken, getToken, isAuthRejection, isNetworkError } from "../lib/api";
import { phIdentify, phReset } from "../lib/posthog";

const AuthCtx = createContext(null);

// Persist the last-known user snapshot so a cold PWA startup can render
// the shell immediately without a blank flash even before /auth/me
// resolves. This is UI-only; the source of truth is still the server.
const USER_CACHE_KEY = "vu_user_cache";
// Founder-only: remembered "View As" role between sessions. Purely a UI
// projection — never sent to the server, never mutates the DB.
const VIEW_AS_KEY = "vu_founder_view_as";
// Founder-only: when starting impersonation, the founder's original access
// token is stashed here so "Return to Founder Account" can restore it
// without a fresh login round-trip.
const IMPERSONATION_KEY = "vu_impersonation";

function readCachedUser() {
    try {
        const raw = localStorage.getItem(USER_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeCachedUser(u) {
    try {
        if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
        else localStorage.removeItem(USER_CACHE_KEY);
    } catch { /* quota / private mode — non-fatal */ }
}

export function AuthProvider({ children }) {
    // Optimistically hydrate from cache so protected routes don't bounce
    // to /login on refresh while /auth/me is in flight.
    const [user, setUser] = useState(() => (getToken() ? readCachedUser() : null));
    const [loading, setLoading] = useState(true);
    // Track whether we've resolved auth at least once — used to avoid
    // hiding the cached user on transient failures.
    const resolvedOnce = useRef(false);

    // Founder-only projection: "View As" mode that flips the sidebar +
    // dashboard between Professional / Student without touching the DB.
    // Persisted in localStorage so the choice survives a reload for the
    // founder alone (regular users never see the switcher, and their
    // `viewAs` stays null).
    const [viewAs, setViewAsState] = useState(() => {
        try { return localStorage.getItem(VIEW_AS_KEY); } catch { return null; }
    });
    const setViewAs = useCallback((role) => {
        try {
            if (role) localStorage.setItem(VIEW_AS_KEY, role);
            else localStorage.removeItem(VIEW_AS_KEY);
        } catch { /* private mode */ }
        setViewAsState(role || null);
    }, []);

    // Impersonation snapshot: whichever session is currently active. When
    // the founder starts impersonating, we stash the founder token here so
    // "Return to Founder Account" can swap it back without a re-login. The
    // impersonation JWT itself carries a short exp — expiry is the source
    // of truth; localStorage merely holds the return trip.
    const [impersonation, setImpersonation] = useState(() => {
        try {
            const raw = localStorage.getItem(IMPERSONATION_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });
    const persistImpersonation = useCallback((snap) => {
        try {
            if (snap) localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(snap));
            else localStorage.removeItem(IMPERSONATION_KEY);
        } catch { /* private mode */ }
        setImpersonation(snap);
    }, []);

    const refresh = useCallback(async () => {
        // Skip /me check while OAuth callback is processing
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const hasToken = !!getToken();
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            writeCachedUser(data);
            resolvedOnce.current = true;
        } catch (err) {
            if (isAuthRejection(err)) {
                // Server explicitly said "not you" — this is a genuine
                // logout signal. Wipe local credentials.
                clearToken();
                writeCachedUser(null);
                setUser(null);
                resolvedOnce.current = true;
            } else if (isNetworkError(err) || (err?.response?.status >= 500)) {
                // Transport failure or backend hiccup — keep the cached
                // user visible so an offline PWA startup or a 30-second
                // network stall doesn't wipe the session. We'll retry on
                // the next navigation / api call.
                if (hasToken && !user) {
                    // Restore from cache if we have a token but no state.
                    const cached = readCachedUser();
                    if (cached) setUser(cached);
                }
            } else {
                // Any other client error against /auth/me while carrying
                // a token counts as an auth failure.
                if (hasToken) {
                    clearToken();
                    writeCachedUser(null);
                    setUser(null);
                }
                resolvedOnce.current = true;
            }
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        refresh();
        // Slide the 30-day session forward every time the user actually
        // returns to the app: on tab focus, on network recovery, and once
        // at cold-start after /auth/me has resolved. This is what makes
        // "active users stay logged in indefinitely" true — the window
        // bumps on every real usage instead of only when <25 days remain.
        const slide = () => {
            if (!getToken()) return;
            api.post("/auth/refresh")
                .then(({ data }) => { if (data?.token) saveToken(data.token); })
                .catch(() => { /* best-effort — never a logout trigger */ });
        };
        const onFocus = () => slide();
        const onOnline = () => { slide(); refresh(); };
        // Slide once shortly after mount so the very first app launch
        // also resets the window (rather than waiting for the next focus).
        const bootSlide = setTimeout(slide, 500);
        window.addEventListener("focus", onFocus);
        window.addEventListener("online", onOnline);
        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("online", onOnline);
            clearTimeout(bootSlide);
        };
    }, []);

    useEffect(() => {
        if (user?.user_id) phIdentify(user.user_id, { role: user.role, email: user.email });
    }, [user?.user_id, user?.role, user?.email]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.token) saveToken(data.token);
        setUser(data);
        writeCachedUser(data);
        // Pull the full user record (preferences, etc.) so any downstream
        // component reads from the same shape as /auth/me. Fire-and-forget;
        // setUser(data) above already unblocks the redirect.
        refresh().catch(() => {});
        return data;
    };

    const signup = async (payload) => {
        const { data } = await api.post("/auth/signup", payload);
        if (data.token) saveToken(data.token);
        setUser(data);
        writeCachedUser(data);
        refresh().catch(() => {});
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // Non-fatal — we clear local state below regardless.
            console.debug("[auth] logout call failed:", e?.message);
        }
        clearToken();
        writeCachedUser(null);
        // Also drop founder-only projections so an unrelated next-user
        // session doesn't inherit them.
        try {
            localStorage.removeItem(VIEW_AS_KEY);
            localStorage.removeItem(IMPERSONATION_KEY);
        } catch { /* private mode */ }
        setViewAsState(null);
        setImpersonation(null);
        phReset();
        setUser(null);
    };

    // ── Impersonation controls ──────────────────────────────────────── //
    // startImpersonation: called from the Founder page after the backend
    // returns a short-lived token. We stash the founder's own token as
    // ``originalToken`` so ``stopImpersonation`` can restore it. Then swap
    // the impersonation token in and refresh so every downstream call
    // authenticates as the target user.
    const startImpersonation = useCallback(async (payload) => {
        const originalToken = getToken();
        const snap = {
            token: payload.token,
            session_id: payload.session_id,
            target: payload.target,
            expires_at: payload.expires_at,
            originalToken,
        };
        persistImpersonation(snap);
        saveToken(payload.token);
        await refresh();
    }, [persistImpersonation, refresh]);

    // stopImpersonation: fire-and-forget audit close (backend stamps
    // ``ended_at``), then flip tokens back to the founder session. Never
    // throws — if the network is dead we still restore locally so the
    // founder never gets stuck in the impersonated view.
    const stopImpersonation = useCallback(async () => {
        const snap = impersonation;
        if (!snap) return;
        try {
            if (snap.session_id) {
                await api.post("/admin/impersonate/stop", { session_id: snap.session_id });
            }
        } catch (e) {
            console.debug("[impersonation] stop endpoint failed (non-fatal):", e?.message);
        }
        persistImpersonation(null);
        if (snap.originalToken) {
            saveToken(snap.originalToken);
        } else {
            clearToken();
        }
        await refresh();
    }, [impersonation, persistImpersonation, refresh]);

    // If the backend ever reports the current user is NOT the impersonation
    // target (e.g. token expired → resolved to something else), or the user
    // dict no longer carries the impersonated_by flag, tidy up local state.
    // Also auto-tear-down if the stored expires_at has elapsed.
    useEffect(() => {
        if (!impersonation) return;
        try {
            const exp = new Date(impersonation.expires_at).getTime();
            if (Number.isFinite(exp) && exp <= Date.now()) {
                stopImpersonation();
                return;
            }
            // Schedule auto-restore at expiry (+2 s buffer).
            const t = setTimeout(stopImpersonation, Math.max(1000, exp - Date.now() + 2000));
            return () => clearTimeout(t);
        } catch { /* malformed timestamp — ignore */ }
    }, [impersonation, stopImpersonation]);

    // The effective role sidebar / dashboard should render as.
    //   * During impersonation → the target user's real role (viewAs OFF).
    //   * Otherwise, if the founder has set a viewAs preference → that.
    //   * Otherwise, the user's real role.
    const effectiveRole = impersonation
        ? (user?.role || null)
        : (user?.is_founder && viewAs) ? viewAs : (user?.role || null);

    return (
        <AuthCtx.Provider value={{
            user, loading, login, signup, logout, refresh, setUser,
            viewAs, setViewAs, effectiveRole,
            impersonation, startImpersonation, stopImpersonation,
        }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
